import { extend } from "dayjs"

export type ReservationNotificationChannel = "sms" | "alimtalk" | "none"

export interface ReservationCreatedResult {
  reservationUuid: string
  reservationDate: string
}

export interface ReservationBaseSerializer {
  reservationUuid?: string
  reservationName?: string
  reservationPhone?: string
  reservationMemo?: string
  reservationSource?: string
  reservationSourceLabel?: string
  reservationDate?: string // 예: "2025-06-15"
  reservationTime?: string // 예: "09:30"
  reservationDt?: string // 예: "2025-06-15 09:30"
  createdDt?: string | null
  updatedDt?: string
  reservationStatus?: string // "예약중" ,"내원" , "예약변경", "예약취소", "노쇼"
  notificationChannel?: ReservationNotificationChannel | null
}

export interface ReservationExtendSerializer extends ReservationBaseSerializer {
  patientId?: number
  patientUuid?: string
  isNewPatient?: boolean
  patientName?: string
  patientGender?: string
  patientPhone?: string
  patientAge?: number
  pinMemo?: string
  doctorUuid?: string
  doctorName?: string
  doctorProfileName?: string
  doctorProfileImage?: string
}

export interface ReservationCreateSerializer extends ReservationBaseSerializer {
  originalReservationUuid?: string | null
  patientUuid?: string | null
  doctorUuid?: string | null
  skipDoctalkStateSync?: boolean
  smsReservedCurrentDay?: boolean
  smsReservedPreviousDay?: boolean
  smsReservedSameDay?: boolean
  smsReservedPreviousDayTime?: string // "HH:mm" 형식, 예: "17:00"
  smsReservedCurrentDayTime?: string // "HH:mm" 형식, 예: "09:10"
  // 각 SMS 타입별 선택한 템플릿 UUID
  smsTemplateSameDayUuid?: string | null
  smsTemplatePreviousDayUuid?: string | null
  smsTemplateCurrentDayUuid?: string | null
}

export interface ReservationResponse {
  code: number
  message: string
  result: ReservationExtendSerializer[]
}

export interface ReservationDailycountSerializer {
  reservationDailycountDate?: string // 예: "2025-06-15"
  reservationDailycountCount?: number // 예약 건수
}

export interface ReservationExceptionSerializer {
  reservationExceptionUuid: string
  exceptionDate: string
  memberUuid: string | null
  memberName?: string | null
  startTime: string
  endTime: string
  note?: string | null
  createdByName?: string | null
  updatedByName?: string | null
  createdDt?: string | null
  updatedDt?: string | null
}

export interface ReservationExceptionRequest {
  exceptionDate: string
  memberUuid: string | null
  startTime: string
  endTime: string
  note: string
}

// export interface ReservationDailycountResponse {
//   code: number
//   message: string
//   result: ReservationDailycountSerializer[]
// }

export const RESERVATION_STATUS = {
  WAITING: "예약대기",
  RESERVED: "예약중",
  VISITED: "내원",
  CANCELED: "예약취소",
  CHANGED: "예약변경",
  NOSHOW: "노쇼",
}

const normalizeReservationPhoneDigits = (value?: string | null) => String(value || "").replace(/\D/g, "")

const getReservationIdentityKey = (reservation?: ReservationExtendSerializer | null) => {
  if (!reservation) {
    return ""
  }

  const patientUuid = String(reservation.patientUuid || "").trim()
  if (patientUuid) {
    return `uuid:${patientUuid}`
  }

  if (reservation.patientId != null) {
    return `id:${String(reservation.patientId)}`
  }

  const patientName = String(reservation.patientName || reservation.reservationName || "").trim()
  const phoneDigits = normalizeReservationPhoneDigits(reservation.patientPhone || reservation.reservationPhone)

  if (!patientName || !phoneDigits) {
    return ""
  }

  return `name-phone:${patientName}:${phoneDigits}`
}

const getReservationDateKey = (reservation?: ReservationBaseSerializer | null) => {
  const reservationDate = String(reservation?.reservationDate || "").trim()
  if (reservationDate) {
    return reservationDate
  }

  const reservationDt = String(reservation?.reservationDt || "").trim()
  if (reservationDt.length >= 10) {
    return reservationDt.slice(0, 10)
  }

  return ""
}

export const isInactiveReservationStatus = (reservationStatus?: string) => {
  return (
    reservationStatus === RESERVATION_STATUS.CHANGED ||
    reservationStatus === RESERVATION_STATUS.CANCELED ||
    reservationStatus === RESERVATION_STATUS.NOSHOW
  )
}

export const getInactiveReservationStatusLabel = (reservationStatus?: string) => {
  if (reservationStatus === RESERVATION_STATUS.CHANGED) {
    return RESERVATION_STATUS.CHANGED
  }

  if (reservationStatus === RESERVATION_STATUS.NOSHOW) {
    return RESERVATION_STATUS.NOSHOW
  }

  return RESERVATION_STATUS.CANCELED
}

export const shouldHideChangedReservationWhenReplacementExists = (
  reservation: ReservationExtendSerializer,
  reservationList: ReservationExtendSerializer[]
) => {
  if (reservation.reservationStatus !== RESERVATION_STATUS.CHANGED) {
    return false
  }

  const reservationIdentityKey = getReservationIdentityKey(reservation)
  const reservationDateKey = getReservationDateKey(reservation)

  if (!reservationIdentityKey || !reservationDateKey) {
    return false
  }

  return reservationList.some(candidate => {
    if (!candidate || candidate.reservationUuid === reservation.reservationUuid) {
      return false
    }

    if (candidate.reservationStatus !== RESERVATION_STATUS.RESERVED && candidate.reservationStatus !== RESERVATION_STATUS.WAITING) {
      return false
    }

    return getReservationIdentityKey(candidate) === reservationIdentityKey && getReservationDateKey(candidate) === reservationDateKey
  })
}
